import {
  Button,
  Card,
  CardBody,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner as HeroSpinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react';
import { DatabaseZap } from 'lucide-react';
import React, { FC, useMemo, useState } from 'react';

import { PageTitle, PageWrapper } from '../../../components';
import { useHeroToast } from '../../../hooks';
import {
  GuestModulesBackfillHistoryItem,
  GuestModulesBackfillRun,
  useBackfillGuestModules,
  useGuestModulesBackfills,
} from '../../../queries';

/** Sekundy z milisekund do zwięzłego zapisu w tabeli, np. "3.2s". */
const formatDuration = (durationMs: number | null): string => {
  if (durationMs == null) return 'brak';
  return `${(durationMs / 1000).toFixed(1)}s`;
};

/** Data z ISO w formacie polskim; endpoint listy podaje ISO albo null. */
const formatWhen = (iso: string | null): string => {
  if (!iso) return 'brak';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'brak';
  return date.toLocaleString('pl-PL');
};

/** Zakres uruchomienia w formie czytelnej dla człowieka. */
const formatScope = (scope: 'all' | 'hotel', hotelId: string | null): string => {
  if (scope === 'all') return 'Wszystkie';
  return hotelId || 'Hotel';
};

export const Service: FC = () => {
  const { showSuccess, showError } = useHeroToast();
  const [hotelId, setHotelId] = useState('');
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [lastResult, setLastResult] = useState<GuestModulesBackfillRun | null>(null);

  const {
    data: runs,
    isLoading: isHistoryLoading,
    refetch: refetchHistory,
  } = useGuestModulesBackfills();

  const { mutate: runBackfill, isPending } = useBackfillGuestModules();

  const trimmedHotelId = useMemo(() => hotelId.trim(), [hotelId]);

  const handleRun = (params: { hotelId?: string }, label: string): void => {
    runBackfill(params, {
      onSuccess: (result) => {
        setLastResult(result);
        showSuccess(
          `Backfill zakończony: ${result.updated} z ${result.total} hoteli, błędów ${result.errorCount}.`,
          label,
        );
        refetchHistory();
      },
      onError: (err) => {
        showError(err?.message || 'Nie udało się uruchomić backfillu', label);
      },
    });
  };

  const handleRunSelected = (): void => {
    if (!trimmedHotelId) return;
    handleRun({ hotelId: trimmedHotelId }, 'Backfill wybranego hotelu');
  };

  const handleConfirmAll = (): void => {
    setConfirmOpen(false);
    handleRun({}, 'Backfill wszystkich hoteli');
  };

  return (
    <PageWrapper>
      <PageTitle title="Service" />

      <p className="text-sm text-default-500 mt-2 mb-5 max-w-[820px]">
        Backfill lustra guestModules: przelicza flagi widoczności sekcji (usługi, faq, oferty,
        dobre praktyki, wizytówki...) dla hoteli. Uruchom po dodaniu nowego modułu, po imporcie
        danych albo gdy sekcje zniknęły.
      </p>

      <Card shadow="sm" className="mb-5">
        <CardBody className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-3">
            <Input
              className="max-w-[320px]"
              label="ID hotelu (opcjonalnie)"
              placeholder="np. hotel123"
              value={hotelId}
              onValueChange={setHotelId}
              isDisabled={isPending}
              isClearable
              onClear={() => setHotelId('')}
            />
            <Button
              color="primary"
              variant="flat"
              isDisabled={!trimmedHotelId || isPending}
              onPress={handleRunSelected}
              startContent={!isPending ? <DatabaseZap size={16} /> : undefined}
              isLoading={isPending}
            >
              Backfill wybrany hotel
            </Button>
            <Button
              color="warning"
              variant="flat"
              isDisabled={isPending}
              onPress={() => setConfirmOpen(true)}
              isLoading={isPending}
            >
              Backfill wszystkie hotele
            </Button>
            {isPending && <HeroSpinner size="sm" aria-label="Trwa backfill" />}
          </div>

          {lastResult && (
            <div className="flex flex-wrap gap-3">
              <Chip size="sm" variant="flat" color="default">
                Zakres:
                {' '}
                {formatScope(lastResult.scope, lastResult.hotelId)}
              </Chip>
              <Chip size="sm" variant="flat" color="default">
                Hoteli:
                {' '}
                {lastResult.total}
              </Chip>
              <Chip size="sm" variant="flat" color="default">
                Zaktualizowano:
                {' '}
                {lastResult.updated}
              </Chip>
              <Chip
                size="sm"
                variant="flat"
                color={lastResult.errorCount > 0 ? 'danger' : 'default'}
              >
                Błędy:
                {' '}
                {lastResult.errorCount}
              </Chip>
              <Chip size="sm" variant="flat" color="default">
                Czas:
                {' '}
                {formatDuration(lastResult.durationMs)}
              </Chip>
              <Chip
                size="sm"
                variant="flat"
                color={lastResult.success ? 'success' : 'danger'}
              >
                {lastResult.success ? 'OK' : 'Błędy'}
              </Chip>
            </div>
          )}
        </CardBody>
      </Card>

      {isHistoryLoading ? (
        <div className="flex justify-center py-10">
          <HeroSpinner aria-label="Wczytywanie historii" />
        </div>
      ) : (
        <Table aria-label="Historia backfillów guestModules" removeWrapper>
          <TableHeader>
            <TableColumn>Kiedy</TableColumn>
            <TableColumn>Kto</TableColumn>
            <TableColumn>Zakres</TableColumn>
            <TableColumn align="end">Hoteli</TableColumn>
            <TableColumn align="end">Zaktualizowano</TableColumn>
            <TableColumn align="end">Błędy</TableColumn>
            <TableColumn align="end">Czas</TableColumn>
            <TableColumn align="center">Status</TableColumn>
          </TableHeader>
          <TableBody
            emptyContent="Brak uruchomień. Uruchom backfill, żeby zobaczyć historię."
            items={runs ?? []}
          >
            {(run: GuestModulesBackfillHistoryItem) => (
              <TableRow key={run.id}>
                <TableCell>{formatWhen(run.startedAt)}</TableCell>
                <TableCell>{run.triggeredByEmail || run.triggeredByUid || 'brak'}</TableCell>
                <TableCell>{formatScope(run.scope, run.hotelId)}</TableCell>
                <TableCell className="text-right">{run.total}</TableCell>
                <TableCell className="text-right">{run.updated}</TableCell>
                <TableCell className="text-right">
                  <span className={run.errorCount > 0 ? 'text-danger font-medium' : ''}>
                    {run.errorCount}
                  </span>
                </TableCell>
                <TableCell className="text-right">{formatDuration(run.durationMs)}</TableCell>
                <TableCell className="text-center">
                  <Chip size="sm" variant="flat" color={run.success ? 'success' : 'danger'}>
                    {run.success ? 'OK' : 'Błędy'}
                  </Chip>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <Modal
        isOpen={isConfirmOpen}
        onOpenChange={(open) => { if (!open) setConfirmOpen(false); }}
        size="md"
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader className="flex items-center gap-2">
                <DatabaseZap size={18} className="text-warning" />
                Backfill wszystkich hoteli
              </ModalHeader>
              <ModalBody>
                <p className="text-sm text-default-600">
                  To przeliczy lustra guestModules dla wszystkich hoteli. Operacja jest ciężka
                  i może chwilę potrwać. Czy na pewno chcesz kontynuować?
                </p>
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={() => setConfirmOpen(false)}>
                  Anuluj
                </Button>
                <Button color="warning" onPress={handleConfirmAll}>
                  Uruchom dla wszystkich
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </PageWrapper>
  );
};
